import type { ModalFuncProps, ModalProps } from 'antd';
import { App, Modal } from 'antd';
import type { ModalSemanticType } from 'antd/es/modal/interface';
import classNames from 'classnames';
import type { PropsWithChildren } from 'react';
import React from 'react';
import type { IntlShape } from 'react-intl';
import { useIntl } from 'react-intl';

import useAnalytics from 'config/analytics';
import DeleteMessage from 'ui/utils/DeleteMessage';
import { uniq } from 'utils/ObjectUtils';

import Alert from '../Alert';
import {
  CONFIRM_STYLE_OVERRIDE,
  confirmStyle,
  largeBodyStyle,
  largeContainerStyle,
  largeModalStyle,
} from './ui.style';

const getContainer = () => {
  let root = document.getElementById('fb-modal-root');
  if (!root) {
    root = document.body.appendChild(document.createElement('div'));
    root.setAttribute('id', 'fb-modal-root');
  }
  return root;
};

type AnalyticsModalEvent = { name: string };

export type FBModalProps = {
  large?: boolean;
  error?: boolean;
  analytics?: AnalyticsModalEvent;
} & ModalProps &
  PropsWithChildren;

const FBModal: React.FC<FBModalProps> = ({
  large,
  error,
  children,
  className,
  analytics,
  ...rest
}) => {
  const { ga: { send } } = useAnalytics();

  React.useEffect(() => {
    if (analytics?.name) {
      send({ category: 'modal', action: analytics.name });
    }
  }, [analytics?.name, send]);

  const justifyBetweenFooter = (large || Number(rest.width) > 800) && !rest?.footer;

  /* antd accepts either an object or a function for `classNames`. Spreading a function yields no
     own keys, so a caller passing the function form used to lose it silently. */
  const mergeClassNames = (inherited?: ModalSemanticType['classNames']) => ({
    ...inherited,
    wrapper: classNames([className, { 'ant-modal-large': large }, inherited?.wrapper]),
    container: classNames({ [largeContainerStyle]: large }, inherited?.container),
    body: classNames({ [largeBodyStyle]: large }, inherited?.body),
    footer: classNames(
      inherited?.footer,
      { 'd-flex align-items-center justify-content-between': justifyBetweenFooter }
    ),
  });

  const callerClassNames = rest.classNames;
  const mergedClassNames = typeof callerClassNames === 'function'
    ? (props: Parameters<typeof callerClassNames>[0]) => mergeClassNames(callerClassNames(props))
    : mergeClassNames(callerClassNames);

  return (
    <Modal
      getContainer={getContainer}
      {...rest}
      className={classNames('pb-4', { [largeModalStyle]: large })}
      classNames={mergedClassNames}
    >
      {error && <Alert type="error" className="mb-4" />}
      {children}
    </Modal>
  );
};

const getCommonProps = (props: ModalFuncProps) => ({
  ...props,
  icon: null,
  styles: CONFIRM_STYLE_OVERRIDE,
  className: classNames(props.className, confirmStyle),
  getContainer,
});

const CUSTOM_ACTIONS = {
  delete: (intl: IntlShape, { okButtonProps, content }: ModalFuncProps) => ({
    title: intl.formatMessage({ id: 'delete', defaultMessage: 'Delete' }),
    okText: intl.formatMessage({ id: 'confirmActions.delete', defaultMessage: 'Yes, delete' }),
    okButtonProps: { danger: true, ...okButtonProps },
    content: <DeleteMessage intl={intl} content={content} />,
  }),
  archive: (intl: IntlShape, { okButtonProps }: ModalFuncProps) => ({
    title: intl.formatMessage({ id: 'archive', defaultMessage: 'Archive' }),
    okText: intl.formatMessage({ id: 'confirmActions.archive', defaultMessage: 'Yes, archive' }),
    okButtonProps: { danger: true, ...okButtonProps },
  }),
};

export const useModal = () => {
  const intl = useIntl();
  const { modal } = App.useApp();

  return React.useMemo(() => {
    const actions = uniq([...Object.keys(modal), ...Object.keys(CUSTOM_ACTIONS)]);
    return actions.reduce(
      (acc, fnName: keyof typeof CUSTOM_ACTIONS) => ({
        ...acc,
        [fnName]: (properties: ModalFuncProps) =>
          modal.confirm({ ...getCommonProps(properties), ...CUSTOM_ACTIONS[fnName]?.(intl, properties) }),
      }),
      {},
    );
  }, [intl, modal]);
};

export default FBModal;
